import AirRoundedIcon from '@mui/icons-material/AirRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import NightsStayRoundedIcon from '@mui/icons-material/NightsStayRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import {
    Box,
    Button,
    Container,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { MessageKey, useI18n } from '../i18n';
import { RootState } from '../store';

import { computeChart } from './astro/chart';
import { interpretChart as interpretBirthChart } from './astro/interpret';
import { SIGN_GLYPHS, SIGNS } from './astro/zodiac';
import { DevDrawButton } from './components/DevDrawButton';
import { ReminderOffer, useReminderToggle } from './components/ReminderToggle';
import { SigilIcon } from './components/SigilIcon';
import { TarotCard } from './components/TarotCard';
import { msUntilMidnight } from './draw';
import { playCue } from './feedback';
import { formatCountdown, useNow } from './hooks';
import { interpretCard } from './interpret';
import { remindersSupported } from './reminders';
import { PAID_SPREADS } from './spreads';
import { dailyFor } from './tarotSlice';

/** The home screen: the card of the day, then the readings that sit behind a video. */
export const HomePage: React.FC = () => {
    const { t, language } = useI18n();
    const navigate = useNavigate();
    const now = useNow(1000);
    const tarot = useSelector((s: RootState) => s.tarot);
    // Recomputed every second, so the card expires at local midnight without a reload.
    const daily = dailyFor(tarot, now);
    const reminders = useReminderToggle();
    const go = (path: string) => {
        playCue('ui');
        navigate(path);
    };

    // The person's own chart (saved without a name) if there is one, else the newest.
    const saved = tarot.charts.find((c) => !c.name) ?? tarot.charts[0];
    const big = useMemo(() => {
        if (!saved) return null;
        try {
            return interpretBirthChart(computeChart(saved.input), language)
                .bigThree;
        } catch {
            return null;
        }
    }, [saved, language]);

    // Sun, rising, moon: the rising sign sits in the middle (absent when the birth time is unknown).
    const ordered = useMemo(() => {
        if (!big) return [];
        const get = (kind: string) => big.find((b) => b.kind === kind);
        return [get('sun'), get('rising'), get('moon')].filter(
            (b): b is NonNullable<typeof b> => Boolean(b)
        );
    }, [big]);

    const revealed = daily?.unlocked ? daily.cards[0] : undefined;
    const face = revealed
        ? interpretCard(revealed.id, revealed.reversed, 'general', language)
        : undefined;

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                position: 'relative',
                zIndex: 1,
            }}
        >
            <Container maxWidth="sm" sx={{ pb: 4 }}>
                {saved && big && (
                    <Box
                        component="button"
                        type="button"
                        onClick={() => go(`/birth-chart/${saved.id}`)}
                        aria-label={t('home.chart.name')}
                        sx={{
                            display: 'block',
                            width: '100%',
                            mt: 1,
                            p: 1.5,
                            textAlign: 'center',
                            color: 'text.primary',
                            font: 'inherit',
                            cursor: 'pointer',
                            background: 'rgba(21,14,36,.82)',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 2,
                            '@keyframes arcana-rise': {
                                from: {
                                    opacity: 0,
                                    transform: 'translateY(24px)',
                                },
                                to: { opacity: 1, transform: 'none' },
                            },
                            // Sun and moon start folded in behind the rising sign, then open out.
                            '@keyframes arcana-from-center': {
                                from: {
                                    opacity: 0,
                                    transform:
                                        'translateX(var(--from)) scale(.6)',
                                },
                                to: { opacity: 1, transform: 'none' },
                            },
                            '@keyframes arcana-appear': {
                                from: { opacity: 0, transform: 'scale(.8)' },
                                to: { opacity: 1, transform: 'none' },
                            },
                            animation: 'arcana-rise .7s ease-out both',
                            '& .slot': {
                                animation:
                                    'arcana-from-center .9s cubic-bezier(.2,.8,.2,1) 1s both',
                            },
                            '& .slot-center': {
                                animation:
                                    'arcana-appear .6s ease-out .6s both',
                            },
                            '@media (prefers-reduced-motion: reduce)': {
                                animation: 'none',
                                '& .slot, & .slot-center': {
                                    animation: 'none',
                                },
                            },
                            '&:hover, &:focus-visible': {
                                borderColor: 'primary.main',
                                outline: 'none',
                            },
                        }}
                    >
                        <Typography variant="h6" component="p">
                            {saved.name
                                ? t('home.hello', { name: saved.name })
                                : t('home.helloAnon')}
                        </Typography>
                        <Stack
                            direction="row"
                            justifyContent="center"
                            spacing={2.5}
                            sx={{ mt: 0.5 }}
                        >
                            {ordered.map((b, i) => (
                                <Box
                                    key={b.kind}
                                    className={
                                        b.kind === 'rising'
                                            ? 'slot-center'
                                            : 'slot'
                                    }
                                    sx={{
                                        '--from':
                                            ordered.length === 3
                                                ? i === 0
                                                    ? '110%'
                                                    : '-110%'
                                                : i === 0
                                                  ? '55%'
                                                  : '-55%',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block' }}
                                    >
                                        {t(`chart.${b.kind}` as MessageKey)}
                                    </Typography>
                                    <Typography color="primary">
                                        <Box component="span" aria-hidden>
                                            {
                                                SIGN_GLYPHS[
                                                    SIGNS.indexOf(
                                                        b.position.signId
                                                    )
                                                ]
                                            }
                                        </Box>{' '}
                                        {b.position.sign}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}

                <Stack
                    alignItems="center"
                    spacing={2}
                    sx={{ pt: 1, pb: 4, textAlign: 'center' }}
                >
                    <Typography
                        variant="overline"
                        color="primary"
                        sx={{ letterSpacing: '0.35em' }}
                    >
                        {t('home.daily.title')}
                    </Typography>
                    <TarotCard
                        cardId={revealed?.id ?? 'major-00'}
                        name={face?.name ?? ''}
                        reversed={revealed?.reversed ?? false}
                        faceUp={Boolean(revealed)}
                        width={156}
                        glow={!daily}
                        label={face?.name ?? t('home.daily.draw')}
                        onClick={() =>
                            go(
                                daily?.unlocked
                                    ? `/reading/${daily.id}`
                                    : '/ritual/daily'
                            )
                        }
                    />
                    {face && (
                        <Typography variant="h6" component="p">
                            {face.name}
                        </Typography>
                    )}
                    <Typography color="text.secondary" sx={{ maxWidth: 420 }}>
                        {!daily
                            ? t('home.daily.intro')
                            : daily.unlocked
                              ? t('home.daily.next', {
                                    time: formatCountdown(msUntilMidnight(now)),
                                })
                              : t('home.daily.sealed')}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() =>
                            go(
                                daily?.unlocked
                                    ? `/reading/${daily.id}`
                                    : '/ritual/daily'
                            )
                        }
                    >
                        {!daily
                            ? t('home.daily.draw')
                            : daily.unlocked
                              ? t('home.daily.view')
                              : t('home.daily.unlock')}
                    </Button>
                    {!reminders.on && remindersSupported() && (
                        <Button
                            size="small"
                            startIcon={<NotificationsActiveRoundedIcon />}
                            onClick={reminders.toggle}
                        >
                            {t('reminders.offer')}
                        </Button>
                    )}
                    {reminders.toast}
                </Stack>

                <Stack spacing={1.5}>
                    <Typography
                        variant="overline"
                        color="primary"
                        sx={{ letterSpacing: '0.35em' }}
                    >
                        {t('home.spreads.title')}
                    </Typography>
                    {PAID_SPREADS.map((id) => (
                        <Paper
                            key={id}
                            component="button"
                            type="button"
                            onClick={() => go(`/ritual/${id}`)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 1.5,
                                textAlign: 'left',
                                color: 'text.primary',
                                font: 'inherit',
                                cursor: 'pointer',
                                background: 'rgba(21,14,36,.82)',
                                '&:hover, &:focus-visible': {
                                    borderColor: 'primary.main',
                                    outline: 'none',
                                },
                            }}
                        >
                            <SigilIcon spread={id} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="h6"
                                    component="span"
                                    sx={{ display: 'block' }}
                                >
                                    {t(`spread.${id}.name` as MessageKey)}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {t(`spread.${id}.desc` as MessageKey)}
                                </Typography>
                            </Box>
                        </Paper>
                    ))}
                    <Paper
                        component="button"
                        type="button"
                        onClick={() =>
                            go(
                                saved
                                    ? `/birth-chart/${saved.id}`
                                    : '/birth-chart'
                            )
                        }
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            textAlign: 'left',
                            color: 'text.primary',
                            font: 'inherit',
                            cursor: 'pointer',
                            background: 'rgba(21,14,36,.82)',
                            '&:hover, &:focus-visible': {
                                borderColor: 'primary.main',
                                outline: 'none',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                flexShrink: 0,
                                display: 'grid',
                                placeItems: 'center',
                                color: 'primary.main',
                            }}
                        >
                            <AutoAwesomeRoundedIcon fontSize="large" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="h6"
                                component="span"
                                sx={{ display: 'block' }}
                            >
                                {t('home.chart.name')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('home.chart.desc')}
                            </Typography>
                        </Box>
                    </Paper>
                    <Paper
                        component="button"
                        type="button"
                        onClick={() => go('/sleep')}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            textAlign: 'left',
                            color: 'text.primary',
                            font: 'inherit',
                            cursor: 'pointer',
                            background: 'rgba(21,14,36,.82)',
                            '&:hover, &:focus-visible': {
                                borderColor: 'primary.main',
                                outline: 'none',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                flexShrink: 0,
                                display: 'grid',
                                placeItems: 'center',
                                color: 'primary.main',
                            }}
                        >
                            <NightsStayRoundedIcon fontSize="large" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="h6"
                                component="span"
                                sx={{ display: 'block' }}
                            >
                                {t('home.sleep.name')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('home.sleep.desc')}
                            </Typography>
                        </Box>
                    </Paper>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        align="center"
                    >
                        {t('home.spreads.note')}
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<HistoryRoundedIcon />}
                        onClick={() => go('/history')}
                        sx={{ alignSelf: 'center' }}
                    >
                        {t('home.history')}
                        {tarot.history.length > 0
                            ? ` (${tarot.history.length})`
                            : ''}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<MenuBookRoundedIcon />}
                        onClick={() => go('/journal')}
                        sx={{ alignSelf: 'center' }}
                    >
                        {t('home.journal')}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<AirRoundedIcon />}
                        onClick={() => go('/breathe')}
                        sx={{ alignSelf: 'center' }}
                    >
                        {t('home.breathe')}
                    </Button>
                </Stack>
            </Container>
            <ReminderOffer />
            <DevDrawButton />
        </Box>
    );
};
